import { useCallback, useEffect, useState } from "react";
import { useExchangeOrderBook } from "./useExchangeOrderBook";
import type { OrderBook } from "@/components/features/trade/OrderBook/types";
import { aggregateOrderBookByPrecision } from "@/utils/orderbook";

interface UseExchangeOrderBookStreamProps {
  exchangeId: string;
  pairSymbol: string;
  /**
   * only used for krakenfutures
   */
  externalMarketId: string;
  enabled?: boolean;
}

/**
 * This hook wraps useExchangeOrderBook and provides orderbook aggregation functionality
 * It follows the same pattern as useHyperliquidOrderBookStream for consistency
 *
 * @param exchangeId - The exchange ID (e.g., "binance", "okx")
 * @param pairSymbol - The pair symbol (e.g., "BTC/USDT")
 * @param enabled - Whether to enable the WebSocket connection
 * @returns The orderbook and the function to aggregate the orderbook by precision
 */
export const useExchangeOrderBookStream = ({
  exchangeId,
  pairSymbol,
  externalMarketId,
  enabled = true,
}: UseExchangeOrderBookStreamProps) => {
  const result = useExchangeOrderBook({
    exchangeId,
    pairSymbol,
    externalMarketId,
    enabled,
  });

  const [orderBook, setOrderBook] = useState<OrderBook | null>(null);

  const orderBookData = result?.orderBookData;
  const isConnected = result?.isConnected ?? false;
  const error = result?.error ?? null;
  const isSupported = result !== null;

  // Clear stale orderbook data when pair or exchange changes
  useEffect(() => {
    setOrderBook(null);
  }, [pairSymbol, exchangeId]);

  // Update local state when new data arrives
  useEffect(() => {
    if (orderBookData && pairSymbol) {
      setOrderBook({
        asks: orderBookData.asks,
        bids: orderBookData.bids,
        timestamp: orderBookData.timestamp,
      });
    }
  }, [orderBookData, pairSymbol]);

  const getAggregatedOrderBook = useCallback(
    (precision: number): OrderBook | null => {
      if (!pairSymbol) return null;
      return aggregateOrderBookByPrecision(orderBook, precision);
    },
    [orderBook, pairSymbol],
  );

  // Cleanup the orderbook when the component unmounts
  useEffect(() => {
    return () => {
      setOrderBook(null);
    };
  }, []);

  return {
    orderBook: orderBook ?? undefined,
    getAggregatedOrderBook,
    isConnected: !!(pairSymbol && enabled) && isConnected,
    isSupported,
    error,
  };
};
